import React, { useContext, useMemo } from 'react';
import api from 'api';
import classNames from 'classnames';
import useCallbackAsync from 'hooks/useCallbackAsync';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import { LEAD_STAGES, LeadStages } from 'models/Lead';
import ResolvePopup from 'components/ResolvePopup';
import { useTranslation } from 'react-i18next';
import ListingContext from '../../../../providers/Listing/Listing.context';
import CheckToggle from 'components/CheckToggle';
import WorkflowPopupNotificationContext from '../../../../providers/WorkflowPopupNotification/WorkflowPopupNotification.context';

import './InterestToggle.styles.scss';

type InterestToggleProps = {
  className?: string;
  step: LeadStages;
};

const InterestToggle: React.FC<InterestToggleProps> = (props) => {
  const { className, step } = props;

  const {
    canManageDeal,
    workflowConfiguration: { _id, workflowConfigurationSteps },
    updateWorkflow,
  } = useContext(ListingContext);
  const { startCountdown, setField } = useContext(
    WorkflowPopupNotificationContext,
  );

  const classes = classNames('interest-toggle', className);

  const { t } = useTranslation();

  const isChecked = useMemo(
    () =>
      (workflowConfigurationSteps || []).find((el) => el.step === step)
        ?.showInterestPopup,
    [step, workflowConfigurationSteps],
  );

  const [updateInterestPopup, isSavingInterestPopup] = useCallbackAsync(
    async (showInterestPopup: boolean) => {
      if (!_id) return;

      setField(step);

      await api.portfolio.seller.saveWorkflowConfiguration(_id, step, {
        showInterestPopup,
      });

      updateWorkflow({ step, showInterestPopup });
      startCountdown();
    },
  );

  const isDisabled = useMemo(
    () => !_id || !canManageDeal || isSavingInterestPopup,
    [_id, canManageDeal, isSavingInterestPopup],
  );

  if (step === LEAD_STAGES.OFFER) return null;

  return (
    <div className={classes}>
      <div className="interest-toggle__content">
        <p className="interest-toggle__label">
          <span>{t('interestPopup')}</span>

          <ResolvePopup onHover popupContent={t('interestPopupDescription')}>
            <InformationcircleIcon />
          </ResolvePopup>
        </p>

        <div id={step}>
          <CheckToggle
            className="interest-toggle__toggle"
            checked={isChecked}
            disabled={isDisabled}
            onChange={({ target: { checked } }) => updateInterestPopup(checked)}
          />
        </div>
      </div>
    </div>
  );
};

export default InterestToggle;
